import React from 'react';
import { Modal, Box, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useSelector } from 'react-redux';
import { DARK_MODE } from '../../constants';
import uploadCardBackground from '../../assets/images/upload_card_background.svg';
import { useResolutionScale } from '../../hooks/useResolutionScale';

const ModalDialog = ({ open, title, onClose, titleActions, children ,modalWidth: customWidth, modalHeight: customHeight  }) => {
	const themeMode = useSelector((state) => state?.theme?.themeMode);
	const isDark = themeMode === DARK_MODE;

	const { scaleValue } = useResolutionScale();

  // ========= Dynamic scaling =========
  const borderRadius = scaleValue(12);
  const paddingY = scaleValue(48);
  const paddingX = scaleValue(24);
  const headerPaddingX = scaleValue(64);
  const sidebarWidth = scaleValue(12); // ~1.5% of width visually
  const sidebarTop = "10%";
  const sidebarBottom = "10%";
  const titleFontSize = scaleValue(124);
  const modalWidth = customWidth || "70%";
const modalHeight = customHeight || "75vh";
  const closeScale = scaleValue(2.0);

	if (!open) return null;

	return (
		<Modal
			open={open}
			onClose={onClose}
			sx={{
				backgroundColor: isDark ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.2)', // search for background and replace with backgroundColor
				backdropFilter: 'blur(8px)',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				overflow: 'auto',
			}}
		>
			<Box
				onClick={(e) => e.stopPropagation()}
				sx={{
					position: 'relative',
					background: isDark ? '#151A1E' : '#f2f2f2',
					borderRadius,
					width: modalWidth,
					height: modalHeight,
					overflow: 'hidden',
					zIndex: 1,
					boxShadow: isDark
						? '0 8px 24px rgba(0, 0, 0, 0.6)'
						: '0 8px 24px rgba(0, 0, 0, 0.2)',
					display: 'flex',
					flexDirection: 'column',
					padding: `${paddingY}px ${paddingX}px`,
					boxSizing: 'border-box',
					color: isDark ? '#e6e6e6' : '#202020',
				}}
			>
				{/* Sidebars */}
				<Box
					sx={{
						position: 'absolute',
						top: sidebarTop,
						bottom: sidebarBottom,
						left: 0,
						width: sidebarWidth,
						backgroundColor: isDark ? '#4e585f' : '#adaca8',
						borderTopRightRadius: scaleValue(8),
						borderBottomRightRadius: scaleValue(8),
						zIndex: 1,
					}}
				/>
				<Box
					sx={{
						position: 'absolute',
						top: sidebarTop,
						bottom: sidebarBottom,
						right: 0,
						width: sidebarWidth,
						backgroundColor: isDark ? '#4e585f' : '#adaca8',
						borderTopLeftRadius: scaleValue(8),
						borderBottomLeftRadius: scaleValue(8),
						zIndex: 1,
					}}
				/>

				{/* Background Wave */}
				<Box
					component="img"
					src={uploadCardBackground}
					alt="background wave"
					sx={{
						position: 'absolute',
						bottom: 0,
						left: 0,
						width: '100%',
						height: 'auto',
						zIndex: 0,
						objectFit: 'cover',
						opacity: isDark ? 0.2 : 1.0,
						pointerEvents: 'none',
					}}
				/>

				{/* Header */}
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						width: '100%',
						mb: scaleValue(4),
						px: `${headerPaddingX}px`,
						zIndex: 2,
					}}
				>
					{/* Left: Title */}
					<Typography
						variant="inherit"
						sx={{
							fontSize: `${scaleValue(24)}px`,
							fontWeight: 600,
							color: isDark ? '#f5f5f5' : '#202020',
							fontFamily: "'Uni Neue', sans-serif",
							lineHeight: 1, 
						}}
					>
						{title}
					</Typography>

					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							gap: scaleValue(12), 
						}}
					>
						{titleActions ? (
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									'& .MuiButton-root': {
										lineHeight: 1.2,
										py: 0.75,
									},
								}}
							>
								{titleActions}
							</Box>
						) : null}

						{/* Close */}
						<IconButton
							onClick={onClose}
							aria-label="close"
							sx={{
								background: 'none',
								border: 'none',
								color: isDark ? '#8a8a8a' : '#acacac',
								transform: `scale(${closeScale})`,
								transition: 'color 0.2s ease',
								'&:hover': { color: isDark ? '#fff' : '#000' },
								zIndex: 2,
							}}
						>
							<CloseIcon fontSize="large" />
						</IconButton>
					</Box>
				</Box>

				{/* Body */}
				<Box
					sx={{
						flex: 1,
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'flex-start',
						zIndex: 2,
					}}
				>
					{children}
				</Box>
			</Box>
		</Modal>
	);
};

export default ModalDialog;
